/**
 * The services the to-do inbox asks (T11-10). `/admin/dashboard` (design/07 §4.7) does not exist
 * yet, so the home page composes the counters every service already serves — each on its own, so
 * one service being down only greys out its rows (the rest of the page works):
 *
 * | source | endpoint | permission |
 * |---|---|---|
 * | payments | `GET /v1/admin/payments/summary` | payments.read |
 * | orders | `GET /v1/admin/orders/summary` | orders.read |
 * | catalog | `GET /v1/admin/catalog/todo-summary` | products.read |
 * | conflicts | `GET /v1/admin/inventory/conflicts?filter[status]=open` | inventory.read |
 * | discrepancies | `GET /v1/admin/inventory/discrepancies?filter[status]=open` | inventory.read |
 * | holds | `GET /v1/admin/reservations?filter[status]=active` (older than 24 h, not carts) | inventory.read |
 * | inbox | `GET /v1/admin/inbox/summary` (`byType`) | inbox.read |
 * | taxfree | `GET /v1/admin/tax-free/refunds?status=failed|due` | tax.taxfree |
 * | settlements | `GET /v1/admin/settlements?status=awaiting_payout` | consignment.read |
 * | kobutsu | `GET /v1/admin/kobutsu-ledger/completeness` | kobutsu.read |
 * | pos | `GET /v1/admin/reports/pos-shifts?from=<7 days ago>` (`varianceApprovedBy` set) | reports.view |
 *
 * Refreshed every 60 s while the tab is visible, and at once when an order arrives (realtime).
 */
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import {
  createCatalogClient,
  createConsignmentClient,
  createInventoryClient,
  createNotificationClient,
  createOrdersClient,
  createPaymentsClient,
  createPosClient,
  createTaxClient,
} from "@tcg/api-client";

import { unwrap } from "@/lib/api/errors";
import { useServiceClient } from "@/lib/api/use-api";
import { usePermissions } from "@/lib/auth/usePermissions";

import { type Count, sourcesFor, type TodoAnswers, type TodoSource } from "./todo";

export const DASHBOARD_REFRESH_MS = 60_000;
export const OLD_HOLD_HOURS = 24;

const PAGE = 100;

function pageCount(page: {
  data: unknown[];
  pageInfo?: { hasNextPage: boolean; totalCount?: number | null } | null;
}): Count {
  if (typeof page.pageInfo?.totalCount === "number") {
    return { n: page.pageInfo.totalCount };
  }
  return { n: page.data.length, more: page.pageInfo?.hasNextPage ?? false };
}

export interface SourceState {
  loading: boolean;
  error: boolean;
  refetch: () => void;
}

export interface TodoSources {
  answers: TodoAnswers;
  states: Partial<Record<TodoSource, SourceState>>;
  /** `today` of the orders summary (orders placed today) — the "new order" fallback counter. */
  ordersToday: number | null;
}

const common = {
  meta: { errorToast: false },
  refetchInterval: DASHBOARD_REFRESH_MS,
  refetchIntervalInBackground: false,
  staleTime: 20_000,
} as const;

function state(query: UseQueryResult<unknown>): SourceState {
  return {
    loading: query.isPending && query.fetchStatus !== "idle",
    error: query.isError,
    refetch: () => void query.refetch(),
  };
}

export function useTodoSources(now: () => Date = () => new Date()): TodoSources {
  const perms = usePermissions();
  const wanted = sourcesFor(perms.can);
  const payments = useServiceClient(createPaymentsClient);
  const orders = useServiceClient(createOrdersClient);
  const catalog = useServiceClient(createCatalogClient);
  const inventory = useServiceClient(createInventoryClient);
  const notification = useServiceClient(createNotificationClient);
  const tax = useServiceClient(createTaxClient);
  const consignment = useServiceClient(createConsignmentClient);
  const pos = useServiceClient(createPosClient);

  const paymentsQ = useQuery({
    ...common,
    queryKey: ["dashboard", "payments-summary"],
    enabled: wanted.has("payments"),
    queryFn: ({ signal }) => unwrap(payments.GET("/v1/admin/payments/summary", { signal })),
  });
  const ordersQ = useQuery({
    ...common,
    queryKey: ["dashboard", "orders-summary"],
    enabled: wanted.has("orders") || perms.can("orders.read"),
    queryFn: ({ signal }) => unwrap(orders.GET("/v1/admin/orders/summary", { signal })),
  });
  const catalogQ = useQuery({
    ...common,
    queryKey: ["dashboard", "catalog-todo"],
    enabled: wanted.has("catalog"),
    queryFn: ({ signal }) => unwrap(catalog.GET("/v1/admin/catalog/todo-summary", { signal })),
  });
  const conflictsQ = useQuery({
    ...common,
    queryKey: ["dashboard", "conflicts"],
    enabled: wanted.has("conflicts"),
    queryFn: async ({ signal }) =>
      pageCount(
        await unwrap(
          inventory.GET("/v1/admin/inventory/conflicts", {
            params: { query: { "filter[status]": "open", limit: PAGE } },
            signal,
          }),
        ),
      ),
  });
  const discrepanciesQ = useQuery({
    ...common,
    queryKey: ["dashboard", "discrepancies"],
    enabled: wanted.has("discrepancies"),
    queryFn: async ({ signal }) =>
      pageCount(
        await unwrap(
          inventory.GET("/v1/admin/inventory/discrepancies", {
            params: { query: { "filter[status]": "open", limit: PAGE } },
            signal,
          }),
        ),
      ),
  });
  const holdsQ = useQuery({
    ...common,
    queryKey: ["dashboard", "old-holds"],
    enabled: wanted.has("holds"),
    queryFn: async ({ signal }): Promise<Count> => {
      const page = await unwrap(
        inventory.GET("/v1/admin/reservations", {
          params: { query: { "filter[status]": "active", limit: PAGE } },
          signal,
        }),
      );
      const cutoff = now().getTime() - OLD_HOLD_HOURS * 3_600_000;
      // carts / checkouts expire by themselves (TTL) — the shop only acts on the others
      const old = page.data.filter(
        (hold) =>
          hold.type !== "cart" && hold.type !== "checkout" && Date.parse(hold.createdAt) < cutoff,
      );
      return { n: old.length, more: page.pageInfo.hasNextPage && old.length === page.data.length };
    },
  });
  const inboxQ = useQuery({
    ...common,
    queryKey: ["dashboard", "inbox-summary"],
    enabled: wanted.has("inbox"),
    queryFn: ({ signal }) => unwrap(notification.GET("/v1/admin/inbox/summary", { signal })),
  });
  const taxfreeQ = useQuery({
    ...common,
    queryKey: ["dashboard", "taxfree"],
    enabled: wanted.has("taxfree"),
    queryFn: async ({ signal }) => {
      const ask = (status: "failed" | "due") =>
        unwrap(
          tax.GET("/v1/admin/tax-free/refunds", {
            params: { query: { status, limit: PAGE } },
            signal,
          }),
        );
      const [failed, due] = await Promise.all([ask("failed"), ask("due")]);
      return { failed: pageCount(failed), due: pageCount(due) };
    },
  });
  const settlementsQ = useQuery({
    ...common,
    queryKey: ["dashboard", "settlements"],
    enabled: wanted.has("settlements"),
    queryFn: async ({ signal }) =>
      pageCount(
        await unwrap(
          consignment.GET("/v1/admin/settlements", {
            params: { query: { status: "awaiting_payout", limit: PAGE } },
            signal,
          }),
        ),
      ),
  });
  const kobutsuQ = useQuery({
    ...common,
    queryKey: ["dashboard", "kobutsu"],
    enabled: wanted.has("kobutsu"),
    queryFn: ({ signal }) =>
      unwrap(
        consignment.GET("/v1/admin/kobutsu-ledger/completeness", {
          params: { query: { limit: 1 } },
          signal,
        }),
      ),
  });

  const posQ = useQuery({
    ...common,
    queryKey: ["dashboard", "pos-variance"],
    enabled: wanted.has("pos"),
    queryFn: async ({ signal }) => {
      const today = new Date(now().getTime() + 9 * 3_600_000);
      const from = new Date(today.getTime() - 6 * 86_400_000).toISOString().slice(0, 10);
      const page = await unwrap(
        pos.GET("/v1/admin/reports/pos-shifts", {
          params: { query: { from, to: today.toISOString().slice(0, 10), format: "json" } },
          signal,
        }),
      );
      const rows = (page as { data: Array<{ varianceApprovedBy: string | null }> }).data;
      return { varianceShifts: rows.filter((r) => r.varianceApprovedBy).length };
    },
  });

  const answers: TodoAnswers = {
    ...(paymentsQ.data
      ? {
          payments: {
            awaiting: paymentsQ.data.awaitingConfirmation.count,
            overdue: paymentsQ.data.overdue.count,
            withNotice: paymentsQ.data.awaitingConfirmation.withNotice,
          },
        }
      : {}),
    ...(ordersQ.data ? { orders: { views: ordersQ.data.views } } : {}),
    ...(catalogQ.data
      ? {
          catalog: {
            noMedia: catalogQ.data.noMedia,
            noPrice: catalogQ.data.noPrice,
            unverifiedCompareAt: catalogQ.data.unverifiedCompareAt,
            pricesToCheck: catalogQ.data.pricesToCheck,
          },
        }
      : {}),
    ...(conflictsQ.data ? { conflicts: conflictsQ.data } : {}),
    ...(discrepanciesQ.data ? { discrepancies: discrepanciesQ.data } : {}),
    ...(holdsQ.data ? { holds: holdsQ.data } : {}),
    ...(inboxQ.data ? { inbox: { byType: inboxQ.data.byType as Record<string, number> } } : {}),
    ...(taxfreeQ.data ? { taxfree: taxfreeQ.data } : {}),
    ...(settlementsQ.data ? { settlements: settlementsQ.data } : {}),
    ...(kobutsuQ.data ? { kobutsu: { incomplete: kobutsuQ.data.incomplete } } : {}),
    ...(posQ.data ? { pos: posQ.data } : {}),
  };

  const states: Partial<Record<TodoSource, SourceState>> = {};
  const all: Array<[TodoSource, UseQueryResult<unknown>]> = [
    ["payments", paymentsQ],
    ["orders", ordersQ],
    ["catalog", catalogQ],
    ["conflicts", conflictsQ],
    ["discrepancies", discrepanciesQ],
    ["holds", holdsQ],
    ["inbox", inboxQ],
    ["taxfree", taxfreeQ],
    ["settlements", settlementsQ],
    ["kobutsu", kobutsuQ],
    ["pos", posQ],
  ];
  for (const [source, query] of all) {
    if (wanted.has(source)) {
      states[source] = state(query);
    }
  }
  return { answers, states, ordersToday: ordersQ.data?.today ?? null };
}
